/**
 * 国际化入口
 *
 * 语言包按模块拆分放在 ./zh-CN 与 ./en-US 目录下，
 * 新增模块时：1. 在两个目录下新增同名 json；2. 在下方 messages 中引入即可。
 * 新增语种时：1. 新建目录与同名 json；2. 在 LANGUAGES / messages / viewUILocales 中补充。
 */
import Vue from 'vue'
import VueI18n from 'vue-i18n'
import ViewUI from 'view-design'

/**
 * view-design（iView）自带语言包
 */
import zhViewUI from 'view-design/dist/locale/zh-CN'
import enViewUI from 'view-design/dist/locale/en-US'
import ptViewUI from 'view-design/dist/locale/pt-BR'
import esViewUI from 'view-design/dist/locale/es-ES'

/**
 * 业务语言包
 */
import zhCommon from './zh-CN/Common.json'
import zhLogin from './zh-CN/Login.json'
import zhLayout from './zh-CN/Layout.json'
import zhMenu from './zh-CN/Menu.json'
import zhEnum from './zh-CN/Enum.json'
import zhAlarm from './zh-CN/AlarmCode.json'
import zhTerminateReason from './zh-CN/TerminateReason.json'
import zhParamItem from './zh-CN/ParameterItem.json'

import enCommon from './en-US/Common.json'
import enLogin from './en-US/Login.json'
import enLayout from './en-US/Layout.json'
import enMenu from './en-US/Menu.json'
import enText from './en-US/Text.json'
import enEnum from './en-US/Enum.json'
import enAlarm from './en-US/AlarmCode.json'
import enTerminateReason from './en-US/TerminateReason.json'
import enParamItem from './en-US/ParameterItem.json'

import ptCommon from './pt-BR/Common.json'
import ptLogin from './pt-BR/Login.json'
import ptLayout from './pt-BR/Layout.json'
import ptMenu from './pt-BR/Menu.json'
import ptText from './pt-BR/Text.json'
import ptEnum from './pt-BR/Enum.json'
import ptAlarm from './pt-BR/AlarmCode.json'
import ptTerminateReason from './pt-BR/TerminateReason.json'
import ptParamItem from './pt-BR/ParameterItem.json'

import esCommon from './es-ES/Common.json'
import esLogin from './es-ES/Login.json'
import esLayout from './es-ES/Layout.json'
import esMenu from './es-ES/Menu.json'
import esText from './es-ES/Text.json'
import esEnum from './es-ES/Enum.json'
import esAlarm from './es-ES/AlarmCode.json'
import esTerminateReason from './es-ES/TerminateReason.json'
import esParamItem from './es-ES/ParameterItem.json'

Vue.use(VueI18n)

/**
 * 支持的语言列表
 */
export const LANGUAGES = [{
		value: 'zh-CN',
		label: '中文'
	},
	{
		value: 'en-US',
		label: 'English'
	},
	{
		value: 'pt-BR',
		label: 'Português'
	},
	{
		value: 'es-ES',
		label: 'Español'
	}
]

export const STORAGE_KEY = 'language'
export const DEFAULT_LANG = 'zh-CN'
// 浏览器标签页标题对应的词条：与页头平台名、登录页标题共用同一个，保证三处一致
export const APP_TITLE_KEY = 'layout_platform_title'

/**
 * 语言版本号（响应式）：每次切换语言自增
 * $tt / $tm / $tw 在渲染过程中读取它，从而建立响应式依赖；
 * 布局里的 <router-view :key="..."> 也依赖它，切换语言时重建当前页面，
 * 让 data()/mounted() 里一次性生成的表格列、下拉项等一起刷新
 */
export const langState = Vue.observable({
	version: 0
})

const messages = {
	'zh-CN': Object.assign({}, zhCommon, zhLogin, zhLayout, zhMenu, zhEnum, zhAlarm, zhTerminateReason, zhParamItem),
	'en-US': Object.assign({}, enCommon, enLogin, enLayout, enMenu, enText, enEnum, enAlarm, enTerminateReason, enParamItem),
	'pt-BR': Object.assign({}, ptCommon, ptLogin, ptLayout, ptMenu, ptText, ptEnum, ptAlarm, ptTerminateReason, ptParamItem),
	'es-ES': Object.assign({}, esCommon, esLogin, esLayout, esMenu, esText, esEnum, esAlarm, esTerminateReason, esParamItem)
}

/**
 * view-design 组件库语言包
 */
const viewUILocales = {
	'zh-CN': zhViewUI,
	'en-US': enViewUI,
	'pt-BR': ptViewUI,
	'es-ES': esViewUI
}

/**
 * 初始语言：本地存储 > 浏览器语言 > 默认中文
 */
function getInitLocale() {
	const saved = window.localStorage.getItem(STORAGE_KEY)
	if (saved && messages[saved]) return saved

	const navLang = navigator.language || navigator.userLanguage || ''
	if (messages[navLang]) return navLang

	const lower = navLang.toLowerCase()
	if (lower.indexOf('zh') === 0) return 'zh-CN'
	if (lower.indexOf('en') === 0) return 'en-US'
	return DEFAULT_LANG
}

export const i18n = new VueI18n({
	locale: getInitLocale(),
	fallbackLocale: DEFAULT_LANG,
	messages,
	silentTranslationWarn: true
})

/**
 * 切换语言：同步 vue-i18n、本地存储、html lang 以及 view-design 组件语言
 * @param {String} lang 语言标识，如 zh-CN / en-US
 */
export function setLocale(lang) {
	const target = messages[lang] ? lang : DEFAULT_LANG
	i18n.locale = target
	// 自增版本号，通知所有用到 $tt/$tm/$tw 的组件重新渲染
	langState.version++
	window.localStorage.setItem(STORAGE_KEY, target)
	if (typeof document !== 'undefined') {
		document.documentElement.lang = target
		// 浏览器标签页标题：取当前语种译文，取不到时回退默认语种
		const title = (messages[target] || {})[APP_TITLE_KEY] || (messages[DEFAULT_LANG] || {})[APP_TITLE_KEY]
		if (title) document.title = title
	}
	if (ViewUI && typeof ViewUI.locale === 'function') {
		ViewUI.locale(viewUILocales[target])
	}
	return target
}

/**
 * 当前语言
 */
export function getLocale() {
	return i18n.locale
}

/**
 * 后端菜单映射字典：menu_dict（菜单 name）、menu_top_dict（分组 topName）、widget_dict（按钮控件 Name）
 * 取值顺序：当前语言包 -> 默认语言包
 */
const MENU_DICT_KEYS = ['menu_dict', 'menu_top_dict']
const WIDGET_DICT_KEYS = ['widget_dict']

const EMPTY_DICT = {}
// 字典缓存：语言版本 + 当前语种作为键，避免每次调用都重新取属性、重复创建空对象
const dictCache = {
	version: -1,
	locale: '',
	map: {}
}

function getDict(dictKey) {
	// 读取版本号：渲染过程中调用会建立响应式依赖，语言切换后组件自动重渲染
	void langState.version
	if (dictCache.version !== langState.version || dictCache.locale !== i18n.locale) {
		dictCache.version = langState.version
		dictCache.locale = i18n.locale
		dictCache.map = {}
	}
	const cached = dictCache.map[dictKey]
	if (cached !== undefined) return cached
	const localeMessages = i18n.messages[i18n.locale] || {}
	const fallbackMessages = i18n.messages[i18n.fallbackLocale] || {}
	const dict = localeMessages[dictKey] || fallbackMessages[dictKey] || EMPTY_DICT
	dictCache.map[dictKey] = dict
	return dict
}

function lookup(dictKeys, key) {
	if (!key || typeof key !== 'string') return undefined
	for (let i = 0; i < dictKeys.length; i++) {
		const dict = getDict(dictKeys[i])
		if (dict[key] !== undefined) return dict[key]
		// 动态路由会在菜单 name 后补 s（如 stationMonitorings），去掉后缀再查一次
		if (key.length > 1 && key.slice(-1) === 's' && dict[key.slice(0, -1)] !== undefined) {
			return dict[key.slice(0, -1)]
		}
	}
	return undefined
}

/**
 * 菜单/分组名称翻译
 * @param {String} key 后端返回的菜单 name 或分组 topName
 * @param {String} fallback 没有映射时使用的原文（DisplayName / title / topName）
 */
export function tMenu(key, fallback) {
	const value = lookup(MENU_DICT_KEYS, key)
	return value !== undefined ? value : (fallback !== undefined ? fallback : key)
}

// 菜单缩写字典（葡/西菜单文字过长时使用：侧边栏窄，缩写展示、悬停看全称）
const MENU_ABBR_DICT_KEYS = ['menu_abbr_dict', 'menu_top_abbr_dict']

/**
 * 菜单短名：优先取缩写字典，没有缩写时返回全称，保证其他语种与未缩写项不受影响
 * 用法：<span :title="$tm(name, title)">{{ $tms(name, title) }}</span>
 * @param {String} key 后端返回的菜单 name 或分组 topName
 * @param {String} fallback 没有映射时使用的原文
 */
export function tMenuShort(key, fallback) {
	const abbr = lookup(MENU_ABBR_DICT_KEYS, key)
	if (abbr !== undefined) return abbr
	return tMenu(key, fallback)
}

// 枚举短写字典：空间很小的场景（如 72px 状态圆）用短写，完整内容用 $tEnum 取
const ENUM_ABBR_DICT_KEY = 'enum_abbr_dict'

/**
 * 枚举短写：优先取短写字典（enum_abbr_dict[枚举名][值]），没有短写时返回完整枚举名
 * 用法：<p :title="$tEnum('WorkStatus', code, desc)">{{$tEnumShort('WorkStatus', code, ...)}}</p>
 */
export function tEnumShort(name, code, fallback) {
	const abbrDict = getDict(ENUM_ABBR_DICT_KEY)
	const group = abbrDict ? abbrDict[name] : undefined
	if (group && code !== undefined && code !== null && code !== '') {
		const key = String(code)
		if (group[key] !== undefined && group[key] !== '') return group[key]
	}
	return tEnum(name, code, fallback)
}

/**
 * 按钮控件（MenuWidgets）名称翻译
 * @param {String} key 后端返回的控件 Name
 * @param {String} fallback 没有映射时使用的原文（Display）
 */
export function tWidget(key, fallback) {
	const value = lookup(WIDGET_DICT_KEYS, key)
	return value !== undefined ? value : (fallback !== undefined ? fallback : key)
}

/**
 * 按中文原文翻译（历史页面里大量硬编码的中文文案用这种方式接入）
 * 中文环境：字典里没有时原样返回，显示效果不变
 * 英文环境：text_dict 中查到则返回英文，查不到回退中文，不会出现空白
 * @param {String} text 中文原文
 */
export function tText(text) {
	if (!text || typeof text !== 'string') return text
	const dict = getDict('text_dict')
	if (dict[text] !== undefined) return dict[text]
	// 兼容「 名称: 」这类带首尾空格/尾部标点的文案，翻译后保留原空格与标点
	const lead = /^\s*/.exec(text)[0]
	const core = text.slice(lead.length)
	const tail = core.replace(/[\s:：]+$/, '')
	if (tail && dict[tail] !== undefined) {
		return lead + dict[tail] + core.slice(tail.length)
	}
	return text
}

/**
 * 后端枚举值翻译（对应后端 DbEnums 里的枚举，如 WorkStatus.cs）
 * 用法：$tEnum('WorkStatus', row.WorkStatus, row.WorkStatusDesc)
 * 第三个参数为兜底值：枚举字典里没有该数值时，退回后端返回的中文描述，不会显示空白
 * @param {String} type 枚举类型名，如 WorkStatus
 * @param {String|Number} value 后端返回的枚举数值
 * @param {String} fallback 未命中时的兜底文案
 */
export function tEnum(type, value, fallback) {
	if (value === undefined || value === null || value === '') {
		return fallback !== undefined && fallback !== null ? fallback : ''
	}
	const dict = getDict('enum_dict')
	const group = dict && dict[type] ? dict[type] : {}
	const key = String(value)
	if (group[key] !== undefined) return group[key]
	// 兼容用枚举成员名（如 Charging）取值的情况
	if (group[value] !== undefined) return group[value]
	return fallback !== undefined && fallback !== null ? fallback : key
}

/**
 * 告警码翻译（对应 define_alarm_code.xlsx 里的告警定义）
 * 用法：$tAlarm(row.AlarmCode, row.AlarmDesc)
 * 第二个参数为兜底值：字典里没有该告警码时，退回后端返回的中文描述，不会显示空白
 * @param {String|Number} code 告警码
 * @param {String} fallback 未命中时的兜底文案
 */
export function tAlarm(code, fallback) {
	if (code === undefined || code === null || code === '') {
		return fallback !== undefined && fallback !== null ? fallback : ''
	}
	const dict = getDict('alarm_dict')
	const key = String(code)
	if (dict && dict[key] !== undefined && dict[key] !== '') return dict[key]
	return fallback !== undefined && fallback !== null ? fallback : key
}

/**
 * 停止原因翻译（对应 define_terminate_reason.xlsx）
 * 用法：$tTerminateReason(row.TerminateReasonCode, row.TerminateReason)
 * 后端返回的是十进制停止原因码，字典的键同样是十进制；未命中时退回后端原文
 * @param {String|Number} code 十进制停止原因码
 * @param {String} fallback 未命中时的兜底文案
 */
export function tTerminateReason(code, fallback) {
	if (code === undefined || code === null || code === '') {
		return fallback !== undefined && fallback !== null ? fallback : ''
	}
	const dict = getDict('terminate_reason_dict')
	const key = String(code)
	if (dict && dict[key] !== undefined && dict[key] !== '') return dict[key]
	return fallback !== undefined && fallback !== null ? fallback : key
}

/**
 * 参数标识项名称翻译（对应 define_parameters_item.xlsx）
 * 用法：$tParamItem(row.DataId, row.DataName)
 * 表里同一个 DataId 可能对应多个名称（A枪/B枪、电表1/电表2），此时字典值是
 * { 中文名: 译文 } 的子字典，用第二参数 DataName 二次匹配
 * 都取不到时回退后端返回的 DataName
 * @param {String|Number} code 参数标识项 DataId
 * @param {String} fallback 未命中时的兜底文案（后端 DataName）
 */
export function tParamItem(code, fallback) {
	const fb = (fallback !== undefined && fallback !== null) ? fallback : ''
	if (code === undefined || code === null || code === '') return fb
	const dict = getDict('parameter_item_dict')
	if (!dict) return fb
	const v = dict[String(code)]
	if (v === undefined || v === '') return fb
	// 一个 DataId 对应多个名称（A枪/B枪等），按 DataName 再匹配一次
	if (typeof v === 'object') return (fb && v[fb]) || fb
	return v
}

// 挂载到 Vue 原型，模板中可直接用 $tm() / $tw() / $tt() / $tEnum() / $tAlarm() / $tTerminateReason() / $tParamItem()（随语言切换自动刷新）
// 注意：不能用 $te，vue-i18n 已占用该名称
Vue.prototype.$tm = tMenu
Vue.prototype.$tms = tMenuShort
Vue.prototype.$tw = tWidget
Vue.prototype.$tt = tText
Vue.prototype.$tEnum = tEnum
Vue.prototype.$tEnumShort = tEnumShort
Vue.prototype.$tAlarm = tAlarm
Vue.prototype.$tTerminateReason = tTerminateReason
Vue.prototype.$tParamItem = tParamItem

// 初始化 view-design 组件语言
setLocale(i18n.locale)

export default i18n
